import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import axios from "axios";
import MediaViewer from "../../components/mediaviewer";
import * as XLSX from "xlsx";

// ---------- HELPERS -------------------------------------------------
const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

const getSchoolYear = (date) => {
    const d = new Date(date);
    const yr = d.getFullYear();
    const mo = d.getMonth(); // 0-Jan � 11-Dec
    return mo < 6 ? `${yr - 1}-${yr}` : `${yr}-${yr + 1}`;
};

const schoolYearToRange = (sy) => {
    const [startYr, endYr] = sy.split("-").map(Number);
    return {
        start: new Date(`${startYr}-07-01T00:00:00`),
        end: new Date(`${endYr}-06-30T23:59:59`),
    };
};

// ---------- ENDPOINTS -----------------------------------------------
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Subcategories";
const API_INCOMES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes";

// --------------------------------------------------------------------
const IncomeMonitoringPage = () => {
    const [categories, setCategories] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [incomes, setIncomes] = useState([]);

    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedSubcategory, setSelectedSubcategory] = useState("all");

    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [mediaImageUrl, setMediaImageUrl] = useState("");

    // ----- school-year state -----
    const currentSY = useMemo(() => getSchoolYear(Date.now()), []);
    const [selectedSchoolYear, setSelectedSchoolYear] = useState(currentSY);
    const [schoolYearOptions, setSchoolYearOptions] = useState([currentSY]);

    // ----- LOAD DATA ------------------------------------------------
    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
        fetchIncomes();
    }, []);

    const fetchCategories = async () => {
        try {
            const { data } = await axios.get(API_CATEGORIES);
            setCategories(
                data.filter(
                    (c) =>
                        c.Type === "Income" &&
                        c.BudgetAmount != null &&
                        Number(c.BudgetAmount) > 0
                )
            );
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const fetchSubcategories = async () => {
        try {
            const { data } = await axios.get(API_SUBCATEGORIES);
            setSubcats(
                data.filter(
                    (s) =>
                        s.Type === "Income" &&
                        s.BudgetAmount != null &&
                        Number(s.BudgetAmount) > 0
                )
            );
        } catch (err) {
            console.error("Error fetching subcategories:", err);
        }
    };

    const fetchIncomes = async () => {
        try {
            const { data } = await axios.get(API_INCOMES);
            setIncomes(data);

            // derive school-year list
            const years = Array.from(
                new Set(
                    data
                        .filter((i) => i.IncomeDate)
                        .map((i) => getSchoolYear(i.IncomeDate))
                )
            ).sort((a, b) => (a < b ? 1 : -1));
            if (!years.includes(currentSY)) years.unshift(currentSY);
            setSchoolYearOptions(years);
        } catch (err) {
            console.error("Error fetching incomes:", err);
        }
    };

    // ----- FILTER BY SCHOOL-YEAR -----------------------------------
    const { start: syStart, end: syEnd } = useMemo(
        () => schoolYearToRange(selectedSchoolYear),
        [selectedSchoolYear]
    );

    const incomesThisSY = useMemo(
        () =>
            incomes.filter(
                (i) =>
                    i.IncomeDate &&
                    new Date(i.IncomeDate) >= syStart &&
                    new Date(i.IncomeDate) <= syEnd
            ),
        [incomes, syStart, syEnd]
    );

    // ----- VALID CATEGORIES / SUBCATS ------------------------------
    const validCatNames = categories.map((c) => c.CategoryName);
    const validSubNames = subcats.map((s) => s.SubcategoryName);

    const allIncomeRecords = incomesThisSY.filter(
        (inc) =>
            validCatNames.includes(inc.IncomeCategory) ||
            validSubNames.includes(inc.IncomeSubcategory)
    );

    // ----- CHART DATA BUILDERS -------------------------------------
    const makeChartData = (items, labelKey) =>
        items.map((item) => {
            const name = item[labelKey];
            const budget = Number(item.BudgetAmount);
            const actual = allIncomeRecords
                .filter((inc) =>
                    labelKey === "CategoryName"
                        ? inc.IncomeCategory === name
                        : inc.IncomeSubcategory === name
                )
                .reduce((s, inc) => s + (Number(inc.Amount) || 0), 0);

            const pct = budget ? Math.min(100, (actual / budget) * 100) : 0;
            return { name, Budget: budget, Actuals: actual, percentUsed: pct };
        });

    const categoryChartData = makeChartData(categories, "CategoryName");
    const subcategoryChartData = makeChartData(subcats, "SubcategoryName");

    const categoryPercentData = categoryChartData.map((d) => ({
        name: d.name,
        Goal: 100,
        Used: d.percentUsed,
    }));
    const subcategoryPercentData = subcategoryChartData.map((d) => ({
        name: d.name,
        Goal: 100,
        Used: d.percentUsed,
    }));

    // ----- OVERALL USAGE -------------------------------------------
    const overallCatPercent = categoryChartData.length
        ? (categoryChartData.reduce((s, d) => s + d.Actuals, 0) /
            categoryChartData.reduce((s, d) => s + d.Budget, 0)) * 100
        : 0;

    // ----- GRID FILTERS --------------------------------------------
    const filteredIncomes = useMemo(() => {
        if (selectedSubcategory !== "all") {
            return allIncomeRecords.filter(
                (inc) => inc.IncomeSubcategory === selectedSubcategory
            );
        }
        if (selectedCategory !== "all") {
            return allIncomeRecords.filter(
                (inc) => inc.IncomeCategory === selectedCategory
            );
        }
        return allIncomeRecords;
    }, [allIncomeRecords, selectedCategory, selectedSubcategory]);

    // ----- STAT BOX PERCENT ----------------------------------------
    let statPercent = overallCatPercent;
    if (selectedSubcategory !== "all") {
        const row = subcategoryChartData.find((d) => d.name === selectedSubcategory);
        statPercent = row ? row.percentUsed : 0;
    } else if (selectedCategory !== "all") {
        const row = categoryChartData.find((d) => d.name === selectedCategory);
        statPercent = row ? row.percentUsed : 0;
    }

    // ----- GRID COLUMNS --------------------------------------------
    const columns = [
        {
            field: "viewReceipt",
            headerName: "View Receipt",
            width: 150,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    sx={{ backgroundColor: "#32CD32", color: "#fff" }}
                    onClick={() => {
                        setMediaImageUrl(params.row.imageURL);
                        setMediaViewerOpen(true);
                    }}
                >
                    View Receipt
                </Button>
            ),
        },
        {
            field: "IncomeDate",
            headerName: "Income Date",
            width: 150,
            valueGetter: (p) =>
                p.row.IncomeDate
                    ? new Date(p.row.IncomeDate).toLocaleDateString()
                    : "",
        },
        {
            field: "Amount",
            headerName: "Amount",
            width: 150,
            valueFormatter: (p) => formatCurrency(p.value || 0),
        },
        { field: "IncomeCategory", headerName: "Category", width: 150 },
        {
            field: "IncomeSubcategory",
            headerName: "Subcategory",
            width: 150,
            valueGetter: (p) => p.row.IncomeSubcategory || "N/A",
        },
        { field: "Description", headerName: "Description", width: 250 },
    ];

    // ----- EXCEL EXPORT --------------------------------------------
    const handleDownloadExcel = () => {
        const wsData = [["Income Date", "Amount", "Category", "Subcategory", "Description"]];
        filteredIncomes.forEach((inc) => {
            wsData.push([
                inc.IncomeDate ? new Date(inc.IncomeDate).toLocaleDateString() : "",
                formatCurrency(inc.Amount || 0),
                inc.IncomeCategory,
                inc.IncomeSubcategory,
                inc.Description,
            ]);
        });
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, "Incomes");
        XLSX.writeFile(wb, `BudgetIncomes_${selectedSchoolYear}.xlsx`);
    };

    // ---------------------------- RENDER ----------------------------
    return (
        <Box p={2}>
            {/* ---------------- STAT + SCHOOL-YEAR PICKER ------------- */}
            <Box
                mb={4}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
            >
                <Box
                    p={2}
                    mb={1}
                    sx={{
                        backgroundColor: "#0F3460",
                        borderRadius: "8px",
                        textAlign: "center",
                        color: "#fff",
                        flexGrow: 1,
                    }}
                >
                    <Typography variant="h6">
                        {selectedSubcategory !== "all"
                            ? `Subcategory (${selectedSubcategory})`
                            : selectedCategory !== "all"
                                ? `Category (${selectedCategory})`
                                : "Overall"}{" "}
                        � {selectedSchoolYear}: {statPercent.toFixed(2)}%
                    </Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 160, ml: 2 }}>
                    <InputLabel>School Year</InputLabel>
                    <Select
                        value={selectedSchoolYear}
                        label="School Year"
                        onChange={(e) => {
                            setSelectedSchoolYear(e.target.value);
                            setSelectedCategory("all");
                            setSelectedSubcategory("all");
                        }}
                    >
                        {schoolYearOptions.map((sy) => (
                            <MenuItem key={sy} value={sy}>
                                {sy}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Typography variant="h4" gutterBottom>
                District Income Monitoring
            </Typography>

            {/* ---------------- CATEGORY % CHART --------------------- */}
            <Box mb={4} height={300}>
                <ResponsiveContainer>
                    <BarChart
                        data={categoryPercentData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        onClick={(e) => {
                            if (e?.activePayload?.length) {
                                const { name } = e.activePayload[0].payload;
                                setSelectedCategory(name);
                                setSelectedSubcategory("all");
                            }
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <RechartsTooltip formatter={(v) => `${v.toFixed(2)}%`} />
                        <Legend />
                        <Bar dataKey="Goal" name="Goal (100%)" fill="#8884d8" radius={[10, 10, 10, 10]} />
                        <Bar dataKey="Used" name="Used (%)" fill="#82ca9d" radius={[10, 10, 10, 10]} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            {/* ---------------- SUBCATEGORY % CHART ------------------ */}
            <Box mb={4} height={300}>
                <ResponsiveContainer>
                    <BarChart
                        data={subcategoryPercentData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        onClick={(e) => {
                            if (e?.activePayload?.length) {
                                const { name } = e.activePayload[0].payload;
                                setSelectedSubcategory(name);
                                setSelectedCategory("all");
                            }
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <RechartsTooltip formatter={(v) => `${v.toFixed(2)}%`} />
                        <Legend />
                        <Bar dataKey="Goal" name="Goal (100%)" fill="#8884d8" radius={[10, 10, 10, 10]} />
                        <Bar dataKey="Used" name="Used (%)" fill="#82ca9d" radius={[10, 10, 10, 10]} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            {/* ---------------- DOWNLOAD BUTTON --------------------- */}
            <Box mb={2}>
                <Button
                    variant="contained"
                    sx={{ backgroundColor: "#FFD700", color: "#000", fontWeight: "bold" }}
                    onClick={handleDownloadExcel}
                >
                    <img
                        src="https://static.vecteezy.com/system/resources/previews/022/100/783/non_2x/microsoft-excel-logo-transparent-free-png.png"
                        alt="Excel"
                        style={{ height: 20, marginRight: 8 }}
                    />
                    Download Excel
                </Button>
            </Box>

            {/* ---------------- DATA GRID --------------------------- */}
            <Box
                mb={2}
                sx={{
                    height: 400,
                    "& .MuiDataGrid-root": { color: "#fff" },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "rgba(255,255,255,0.1)!important",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "rgba(255,255,255,0.2)!important",
                    },
                    "& .MuiDataGrid-footerContainer": { backgroundColor: "#0F3460", color: "#fff" },
                }}
            >
                <DataGrid
                    rows={filteredIncomes}
                    columns={columns}
                    getRowId={(r) => r.apiid}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Box>

            {/* ---------------- TOTAL FOOTER ------------------------ */}
            <Box mt={2} sx={{ textAlign: "right", fontSize: "1.2rem", color: "#fff" }}>
                Total Incomes:{" "}
                {formatCurrency(
                    filteredIncomes.reduce((s, inc) => s + (Number(inc.Amount) || 0), 0)
                )}
            </Box>

            {/* ---------------- MEDIA VIEWER ------------------------ */}
            {mediaViewerOpen && (
                <MediaViewer
                    open={mediaViewerOpen}
                    src={mediaImageUrl}
                    onClose={() => setMediaViewerOpen(false)}
                    type="image"
                />
            )}
        </Box>
    );
};

export default IncomeMonitoringPage;
